import React, {createContext, useContext, useEffect, useMemo, useState} from "react";
import { useLoginUser, useLoginUserWithOAuth} from "../hooks/loginUser/loginUser";
import {useRegisterUser} from "../hooks/registerUser/registerUser";
import {parseJwt} from "../utils/helperFunctions";
import {ApolloLink, HttpLink} from "@apollo/client";
import {isTokenExpired} from "../utils/timeUtils";
import {useGetNcaabbMatchups} from "../hooks/getNcaabbMatchups/getNcaabbMatchups";
import {useGetNcaafbMatchups} from "../hooks/getNcaafbMatchups/getNcaafbMatchups";
import {useGetNbaMatchups} from "../hooks/getNbaMatchups/getNbaMatchups";
import {useGetNflMatchups} from "../hooks/getNflMatchups/getNflMatchups";
import {useGetUserTrends} from "../hooks/getMyTrends/getMyTrends";
import {useGetDefaultTrends} from "../hooks/getDefaultTrends/getDefaultTrends";

interface AuthContextType {
    loggedInUserEmail: string;
    loggedInUserProfilePicture: string;
    login: (email: string, password: string) => Promise<any>;
    loginWithOAuth: (accessToken: string) => Promise<any>;
    resetLoginError: () => any;
    logout: () => void;
    register: (email: string, password: string) => Promise<any>;
    setUserToken: (token: string) => void;
    loginError: any;
    registrationError: any;
}

const AuthContext = createContext<AuthContextType>({
    loggedInUserEmail: '',
    loggedInUserProfilePicture: '',
    login: async () => ({
        data: {}
    }),
    loginWithOAuth: async () => {
    },
    logout: () => {
    },
    register: async () => {
    },
    resetLoginError: () => {
    },
    setUserToken: (token: string) => {
    },
    loginError: '',
    registrationError: '',
});

export const useAuth = () => useContext(AuthContext);
const getAuthToken = () => localStorage.getItem('token');

export const authLink = new ApolloLink((operation, forward) => {
    const token = getAuthToken();
    operation.setContext({
        headers: {
            authorization: token ? `Bearer ${token}` : '',
        }
    });

    return forward(operation);
});

const apiUrl = 'https://trend-spotting-sdql-and-db-api-70afa0637d79.herokuapp.com/graphql'
// const apiUrl = 'http://localhost:4000/graphql'
const httpLink = new HttpLink({uri: apiUrl});
export const link = ApolloLink.from([authLink, httpLink]);

export const AuthProvider = (
        {
            children,
            currentLocation
        }:
        { 
            children: any, 
            currentLocation: string 
        }
    ) => {
    let parsedJwt: any;
    let sport = 'nba';
    if(currentLocation.includes('ncaafb')) sport = 'ncaafb'
    if(currentLocation.includes('nba')) sport = 'nba'
    if(currentLocation.includes('nfl')) sport = 'nfl'
    if(currentLocation.includes('ncaabb')) sport = 'ncabb'
    if (localStorage.getItem('token')) {
        parsedJwt = parseJwt(localStorage.token)
    }
    let emailToUse = isTokenExpired(parsedJwt) ? null : parsedJwt?.email;
    console.log("isTokenExpired(parsedJwt): ", isTokenExpired(parsedJwt))
    const [loggedInUserEmail, setLoggedInUserEmail] = useState(emailToUse);
    const [loggedInUserProfilePicture, setLoggedInUserProfilePicture] = useState(parsedJwt?.profilePictureUrl);
    const {registerUser, data: userRegistrationData, error: registrationError} = useRegisterUser()
    const {loginUser, data: userLoginData, error: loginError, reset: resetLoginError } = useLoginUser()
    const {loginUserWithOAuth} = useLoginUserWithOAuth()
    const {refetch: refetchNcaabb} = useGetNcaabbMatchups()
    const {refetch: refetchNcaafb} = useGetNcaafbMatchups()
    const {refetch: refetchNba} = useGetNbaMatchups()
    const {refetch: refetchNfl} = useGetNflMatchups()
    const {refetch: refetchDefaultTrends} = useGetDefaultTrends(sport)
    const {refetch: refetchUserTrends} = useGetUserTrends()
    let refetchToUse = refetchNcaabb;
    if(sport === 'ncaafb') refetchToUse = refetchNcaafb
    if(sport === 'ncaabb') refetchToUse = refetchNcaabb
    if(sport === 'nba') refetchToUse = refetchNba
    if(sport === 'nfl') refetchToUse = refetchNfl
    if(currentLocation.includes('my-trends')) refetchToUse = refetchUserTrends

    const register = async (email: string, password: string) => {
        try {
            const response = await registerUser(email, password);
            if (response.data?.registerUser?.token) {
                setLoggedInUserEmail(response.data?.registerUser?.email);
                setUserToken(response.data.registerUser.token);
                refetchToUse()
                refetchDefaultTrends()
            }
            return response;
        } catch (err) {
            console.error("Registration error:", err);
        }
    };

    const login = async (email: string, password: string) => {
        try {
            const response = await loginUser(email, password);
            if (response.data?.loginUser?.token) {
                setLoggedInUserEmail(response.data?.loginUser?.email)
                setUserToken(response.data.loginUser.token);
                refetchToUse()
                refetchDefaultTrends()
            }
            return response;
        } catch (err) {
            console.error("Login error:", err);
        }
    };

    const setUserToken = (token: string | null) => {
        if (token) {
            localStorage.setItem("token", token);
        } else {
            localStorage.removeItem("token");
        }
        const parsedJwt = token ? parseJwt(token) : null;
        setLoggedInUserEmail(parsedJwt?.email || null);
        setLoggedInUserProfilePicture(parsedJwt?.profilePictureUrl || null);
    };

    const loginWithOAuth = async (accessToken: string) => {
        try {
            const response = await loginUserWithOAuth(accessToken);
            if (response.data?.loginUserWithOAuth?.token) {
                const newToken = response.data.loginUserWithOAuth.token;
                setUserToken(newToken); // This will trigger state updates and re-renders
            }
        } catch (err) {
            console.error("OAuth login error:", err);
        }
    };

    const logout = () => {
        localStorage.setItem("token", "");
        localStorage.clear()
        setLoggedInUserEmail("");
        setLoggedInUserProfilePicture("");
        refetchToUse()
        refetchDefaultTrends()
    };

    const token = localStorage.getItem("token");
    const isTokenExpiredMemoized: boolean = useMemo(() => {
        return isTokenExpired(parsedJwt);
    }, [parsedJwt]);

    useEffect(() => {
        const interval = setInterval(() => {
            const token = localStorage.getItem("token");
            const parsed = token ? parseJwt(token) : null;
            if (parsed && isTokenExpired(parsed)) {
                logout();
            }
        }, 30 * 1000);

        return () => clearInterval(interval);
    }, []);

    return (
        <AuthContext.Provider
            value={{
                login,
                loginWithOAuth,
                logout,
                register,
                loginError,
                resetLoginError,
                registrationError,
                loggedInUserProfilePicture,
                loggedInUserEmail,
                setUserToken,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
